<template>
  <div>
    <div class="flex flex-col gap-1 pb-2.5 pt-8 px-5 relative shrink-0 w-full">
      <button
        class="absolute right-5 top-3 flex items-center p-1 rounded-[4px] text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
        @click="$emit('toggle-panel')"
      >
        <span class="material-symbols-outlined text-[20px]/[20px]">close</span>
      </button>
      <div class="flex flex-col justify-between gap-[18px] w-full">
        <!-- Avatar + Name + Bio + Contact Info -->
        <div class="flex flex-col gap-5 w-full">
          <!-- Avatar + Name -->
          <div class="flex flex-col gap-2 w-full">
            <div class="flex gap-2 items-center w-full">
              <thumbnail
                :src="contact.thumbnail"
                size="64px"
                :username="contact.name"
                class="shrink-0"
              />
              <div
                class="flex flex-col items-start relative flex-1 min-w-0 gap-0.5"
              >
                <div>
                  <span
                    v-if="contactBlocked"
                    v-tooltip.top-start="$t('CONTACT_PANEL.BLOCKED_SUCCESS')"
                    class="semantic-tag small tag-secondary-05 inline-block cursor-pointer"
                    @click="$router.push(contactProfileLink)"
                  >
                    Blocked
                  </span>
                </div>
                <div
                  class="flex-1 min-w-0 w-fit text-[20px]/[24px] font-bold text-gray-600 relative"
                  @click="$router.push(contactProfileLink)"
                >
                  <div
                    class="line-clamp-2 max-w-[210px] cursor-pointer hover:underline"
                  >
                    {{ contact.name }}
                  </div>
                  <div
                    class="flex flex-row items-center gap-1 absolute bottom-1/2 translate-y-1/2 ltr:right-[-22px] rtl:left-[-22px]"
                  >
                    <span
                      v-if="contact.created_at"
                      v-tooltip.top-start="
                        `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                          contact.created_at
                        )}`
                      "
                      class="material-symbols-outlined p-[2px] text-[16px] cursor-default"
                    >
                      info
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bio -->
            <div v-if="bio" class="flex flex-col gap-1 w-full">
              <p
                ref="bioTextRef"
                class="text-sm text-gray-600 leading-5 break-words mb-0"
                :class="{ 'line-clamp-2': !showFullBio }"
              >
                {{ bio }}
              </p>
              <button
                v-if="bioOverflowing"
                class="flex items-center gap-1 text-sm text-green-500 self-start"
                @click="showFullBio = !showFullBio"
              >
                {{
                  showFullBio
                    ? $t('CONTACT_PANEL.READ_LESS')
                    : $t('CONTACT_PANEL.READ_MORE')
                }}
                <span
                  class="material-symbols-outlined text-[16px] transition-transform"
                  :class="showFullBio ? 'rotate-180' : ''"
                >
                  expand_more
                </span>
              </button>
            </div>
          </div>
          <!-- Contact info 2-column grid -->
          <div>
            <div class="flex gap-5 w-full">
              <!-- Left column -->
              <div
                v-if="contact.email || contact.phone_number || showWaIdRow"
                class="flex flex-1 min-w-0 flex-col gap-3"
              >
                <div
                  v-if="contact.email"
                  class="flex flex-row items-center gap-1"
                >
                  <a
                    :href="`mailto:${contact.email}`"
                    class="flex flex-1 min-w-0 gap-1 items-center hover:text-green-500 group"
                  >
                    <span
                      class="material-symbols-outlined text-[16px] text-gray-600 shrink-0 group-hover:text-green-500"
                    >
                      mail
                    </span>
                    <span
                      ref="emailTextRef"
                      v-tooltip="{
                        content: truncated.email ? contact.email : '',
                        placement: 'top',
                      }"
                      class="flex-1 min-w-0 text-xs text-gray-600 truncate group-hover:text-green-500"
                    >
                      {{ contact.email }}
                    </span>
                  </a>
                  <span
                    class="material-symbols-outlined text-[14px] text-gray-600 shrink-0 cursor-pointer p-0.5 rounded-[4px] hover:bg-gray-200"
                    @click.stop="onCopy(contact.email)"
                  >
                    content_copy
                  </span>
                </div>
                <div
                  v-if="contact.phone_number"
                  class="flex flex-row items-center gap-1"
                >
                  <a
                    :href="`tel:${contact.phone_number}`"
                    class="flex flex-1 min-w-0 gap-1 items-center hover:text-green-500 group"
                  >
                    <span
                      class="material-symbols-outlined text-[16px] text-gray-600 shrink-0 group-hover:text-green-500"
                    >
                      call
                    </span>
                    <span
                      ref="phoneTextRef"
                      v-tooltip="{
                        content: truncated.phone ? contact.phone_number : '',
                        placement: 'top',
                      }"
                      class="flex-1 min-w-0 text-xs text-gray-600 truncate group-hover:text-green-500"
                    >
                      {{ contact.phone_number }}
                    </span>
                  </a>
                  <span
                    class="material-symbols-outlined text-[14px] text-gray-600 shrink-0 cursor-pointer p-0.5 rounded-[4px] hover:bg-gray-200"
                    @click.stop="onCopy(contact.phone_number)"
                  >
                    content_copy
                  </span>
                </div>
                <div
                  v-if="showWaIdRow"
                  class="flex flex-row items-center gap-1"
                >
                  <span
                    v-tooltip="{
                      content: $t('CONTACT_PANEL.WHATSAPP_ID'),
                      placement: 'top',
                    }"
                    class="material-symbols-outlined text-[16px] text-gray-600 shrink-0"
                  >
                    chat
                  </span>
                  <span
                    ref="waIdTextRef"
                    v-tooltip="{
                      content: truncated.waId ? contact.wa_id : '',
                      placement: 'top',
                    }"
                    class="flex-1 min-w-0 text-xs text-gray-600 truncate"
                  >
                    {{ contact.wa_id }}
                  </span>
                  <span
                    class="material-symbols-outlined text-[14px] text-gray-600 shrink-0 cursor-pointer p-0.5 rounded-[4px] hover:bg-gray-200"
                    @click.stop="onCopy(contact.wa_id)"
                  >
                    content_copy
                  </span>
                </div>
              </div>

              <!-- Right column -->
              <div
                v-if="location || birthdate"
                class="flex flex-1 min-w-0 flex-col gap-3"
              >
                <div v-if="location" class="flex gap-1 items-center">
                  <span
                    class="material-symbols-outlined text-[16px] text-gray-600 shrink-0"
                  >
                    location_on
                  </span>
                  <span
                    ref="locationTextRef"
                    v-tooltip="{
                      content: truncated.location ? location : '',
                      placement: 'top',
                    }"
                    class="flex-1 min-w-0 text-xs text-gray-600 truncate"
                    >{{ location }}</span
                  >
                </div>
                <div v-if="birthdate" class="flex gap-1 items-center">
                  <span
                    class="material-symbols-outlined text-[16px] text-gray-600 shrink-0"
                  >
                    calendar_today
                  </span>
                  <span class="flex-1 min-w-0 text-xs text-gray-600">{{
                    birthdate
                  }}</span>
                </div>
              </div>
            </div>
            <div v-if="companyName" class="flex gap-1 items-center mt-3">
              <span
                class="material-symbols-outlined text-[16px] text-gray-600 shrink-0"
              >
                corporate_fare
              </span>
              <span
                ref="companyTextRef"
                v-tooltip="{
                  content: truncated.company ? companyName : '',
                  placement: 'top',
                }"
                class="flex-1 min-w-0 text-xs text-gray-600 truncate"
                >{{ companyName }}</span
              >
            </div>
            <social-icons :social-profiles="socialProfiles" class="mt-2" />
          </div>
        </div>

        <!-- Send Message button -->
        <button
          class="flex items-center justify-center gap-1 bg-green-500 hover:bg-green-600 text-white text-sm h-8 w-full rounded-[5px] transition-colors cursor-pointer"
          @click="$emit('open-new-messsage')"
        >
          <span class="material-symbols-outlined text-[16px]">send</span>
          {{ $t('CONTACT_PANEL.SEND_MESSAGE') }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from 'dashboard/routes/dashboard/conversation/contact/SocialIcons.vue';
import timeMixin from 'dashboard/mixins/time';
import alertMixin from 'shared/mixins/alertMixin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export default {
  components: {
    Thumbnail,
    SocialIcons,
  },
  mixins: [timeMixin, alertMixin],
  props: {
    contact: {
      type: Object,
      required: true,
    },
    showWaId: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showFullBio: false,
      bioOverflowing: false,
      showConversationModal: false,
      truncated: {
        email: false,
        phone: false,
        waId: false,
        location: false,
        company: false,
      },
    };
  },
  computed: {
    showWaIdRow() {
      return this.showWaId && !!this.contact.wa_id;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    bio() {
      return this.contact.bio || this.additionalAttributes.description || '';
    },
    companyName() {
      return this.additionalAttributes.company_name || '';
    },
    location() {
      const { city = '', country = '' } = this.additionalAttributes;
      return [city, country].filter(Boolean).join(', ');
    },
    birthdate() {
      return this.additionalAttributes.birthdate || '';
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
      } = this.additionalAttributes;

      return { twitter: twitterScreenName, ...(socialProfiles || {}) };
    },
    contactBlocked() {
      return this.contact?.blocked;
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
  },
  watch: {
    contact() {
      this.showFullBio = false;
      this.$nextTick(this.updateOverflowState);
    },
    bio() {
      this.$nextTick(this.updateBioOverflow);
    },
  },
  mounted() {
    this.$nextTick(this.updateOverflowState);
    window.addEventListener('resize', this.updateOverflowState);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateOverflowState);
  },
  methods: {
    async onCopy(value) {
      await copyTextToClipboard(value);
      this.showAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    updateOverflowState() {
      this.updateTruncatedState();
      this.updateBioOverflow();
    },
    updateTruncatedState() {
      const refsByKey = {
        email: this.$refs.emailTextRef,
        phone: this.$refs.phoneTextRef,
        waId: this.$refs.waIdTextRef,
        location: this.$refs.locationTextRef,
        company: this.$refs.companyTextRef,
      };
      Object.keys(refsByKey).forEach(key => {
        const el = refsByKey[key];
        this.truncated[key] = !!el && el.scrollWidth > el.clientWidth;
      });
    },
    updateBioOverflow() {
      const el = this.$refs.bioTextRef;
      if (!el) {
        this.bioOverflowing = false;
        return;
      }
      if (this.showFullBio) return;
      const clampedHeight = el.clientHeight;
      el.style.webkitLineClamp = 'unset';
      const fullHeight = el.scrollHeight;
      el.style.webkitLineClamp = '';
      this.bioOverflowing = fullHeight > clampedHeight + 1;
    },
  },
};
</script>
